/**
 * Servicio de configuración para LivenessV7
 * Mapea la configuración del backend a la estructura interna del componente
 */

// Configuración por defecto
const DEFAULT_CONFIG = {
  messages: {},
  messagesEnglish: {},
  closeIcon: {
    show: false,
    url: '',
    size: 30,
    color: '#ffffff'
  },
  typeCamera: 'user',
  informativeText: null,
  timeout: 30,
  backgroundInMessage: 'false',
  showBack: false,
  showFlash: false,
  SPF: true,
  styles: {
    backgroundColor: '#1F2639',
    messageColor: '#ffffff',
    messageBackgroundColor: '#1F2639',
    fontSize: '20px',
    guideColor: '#FFFFFF',
    successColor: '#ACFFB7',
    errorColor: '#ed0050',
    messageErrorColor: '#2B3550'
  },
  sdk: {
    mask_hidden: false,
    background_color: 'rgba(0, 0, 0, 0)',
    auto_close_disabled: true,
    auto_capture_disabled: true,
    capture_mode: 'encryptedPayload',
    payload_size: 'normal',
    device_id: '',
    oval_width: '0.5',
    oval_height: '0.55',
    oval_color: '#FFFFFF',
    oval_stroke_width: '3px'
  },
  camera: {
    width: { ideal: 1080, max: 1280 },
    height: { ideal: 1080, max: 1080 },
    frameRate: { ideal: 60, max: 30 },
    facingMode: 'user'
  },
  validations: {
    sunglasses_detected_check_enabled: true,
    face_not_centered_check_enabled: true,
    dark_image_check_enabled: true,
    face_blurry_check_enabled: true,
    eyes_closed_check_enabled: true,
    mouth_open_check_enabled: true
  },
  timing: {
    okFramesTarget: 12,
    autoCloseDelayMs: 1500,
    passProbabilityThreshold: 0.999,
    timeout: 30
  }
};

/**
 * Mapea la configuración del backend al formato esperado
 * @param {Object} backendConfig - Configuración recibida del servicio
 * @returns {Object} Configuración mapeada
 */
export const mapBackendConfig = (backendConfig) => {
  if (!backendConfig) return DEFAULT_CONFIG;

  return {
    messages: backendConfig.messages || DEFAULT_CONFIG.messages,
    messagesEnglish: backendConfig.messagesEnglish || DEFAULT_CONFIG.messagesEnglish,
    closeIcon: {
      show: backendConfig.closeIcon?.show ?? DEFAULT_CONFIG.closeIcon.show,
      url: backendConfig.closeIcon?.url || DEFAULT_CONFIG.closeIcon.url,
      size: backendConfig.closeIcon?.size || DEFAULT_CONFIG.closeIcon.size,
      color: backendConfig.closeIcon?.color || DEFAULT_CONFIG.closeIcon.color
    },
    typeCamera: backendConfig.typeCamera || DEFAULT_CONFIG.typeCamera,
    informativeText: backendConfig.informativeText,
    timeout: backendConfig.timeout || backendConfig.timing?.timeout || DEFAULT_CONFIG.timeout,
    backgroundInMessage: backendConfig.backgroundInMessage || DEFAULT_CONFIG.backgroundInMessage,
    showBack: backendConfig.showBack ?? DEFAULT_CONFIG.showBack,
    showFlash: backendConfig.showFlash ?? DEFAULT_CONFIG.showFlash,
    SPF: backendConfig.SPF ?? DEFAULT_CONFIG.SPF,
    styles: {
      backgroundColor: backendConfig.styles?.backgroundColor || DEFAULT_CONFIG.styles.backgroundColor,
      messageColor: backendConfig.styles?.messageColor || DEFAULT_CONFIG.styles.messageColor,
      messageBackgroundColor: backendConfig.styles?.messageBackgroundColor || DEFAULT_CONFIG.styles.messageBackgroundColor,
      fontSize: backendConfig.styles?.fontSize || DEFAULT_CONFIG.styles.fontSize,
      guideColor: backendConfig.styles?.guideColor || DEFAULT_CONFIG.styles.guideColor,
      successColor: backendConfig.styles?.successColor || DEFAULT_CONFIG.styles.successColor,
      errorColor: backendConfig.styles?.errorColor || DEFAULT_CONFIG.styles.errorColor,
      messageErrorColor: backendConfig.styles?.messageErrorColor || DEFAULT_CONFIG.styles.messageErrorColor
    },
    sdk: {
      mask_hidden: backendConfig.sdk?.mask_hidden ?? DEFAULT_CONFIG.sdk.mask_hidden,
      background_color: backendConfig.sdk?.background_color || DEFAULT_CONFIG.sdk.background_color,
      auto_close_disabled: backendConfig.sdk?.auto_close_disabled ?? DEFAULT_CONFIG.sdk.auto_close_disabled,
      auto_capture_disabled: backendConfig.sdk?.auto_capture_disabled ?? DEFAULT_CONFIG.sdk.auto_capture_disabled,
      capture_mode: backendConfig.sdk?.capture_mode || DEFAULT_CONFIG.sdk.capture_mode,
      payload_size: backendConfig.sdk?.payload_size || DEFAULT_CONFIG.sdk.payload_size,
      device_id: backendConfig.sdk?.device_id || DEFAULT_CONFIG.sdk.device_id,
      oval_width: backendConfig.sdk?.oval_width || DEFAULT_CONFIG.sdk.oval_width,
      oval_height: backendConfig.sdk?.oval_height || DEFAULT_CONFIG.sdk.oval_height,
      oval_color: backendConfig.styles?.guideColor || backendConfig.sdk?.oval_color || DEFAULT_CONFIG.sdk.oval_color,
      oval_stroke_width: backendConfig.sdk?.oval_stroke_width || DEFAULT_CONFIG.sdk.oval_stroke_width
    },
    camera: {
      width: backendConfig.camera?.width || DEFAULT_CONFIG.camera.width,
      height: backendConfig.camera?.height || DEFAULT_CONFIG.camera.height,
      frameRate: backendConfig.camera?.frameRate || DEFAULT_CONFIG.camera.frameRate,
      facingMode: backendConfig.camera?.facingMode || DEFAULT_CONFIG.camera.facingMode
    },
    validations: {
      sunglasses_detected_check_enabled: true,
      face_not_centered_check_enabled: true,
      dark_image_check_enabled: true,
      face_blurry_check_enabled: true,
      eyes_closed_check_enabled: true,
      mouth_open_check_enabled: true
    },
    timing: {
      okFramesTarget: backendConfig.timing?.okFramesTarget || DEFAULT_CONFIG.timing.okFramesTarget,
      autoCloseDelayMs: backendConfig.timing?.autoCloseDelayMs || DEFAULT_CONFIG.timing.autoCloseDelayMs,
      passProbabilityThreshold: backendConfig.timing?.passProbabilityThreshold || DEFAULT_CONFIG.timing.passProbabilityThreshold,
      timeout: backendConfig.timing?.timeout || backendConfig.timeout || DEFAULT_CONFIG.timing.timeout
    }
  };
};

/**
 * Obtiene los estilos dinámicos basados en la configuración
 * @param {Object} config - Configuración mapeada
 * @returns {Object} Estilos CSS
 */
export const getStylesFromConfig = (config) => {
  const styles = config?.styles || DEFAULT_CONFIG.styles;

  return {
    loaderOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: styles.backgroundColor,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      margin: 0,
      padding: 0,
    },
    headerText: {
      position: 'fixed',
      top: 'calc(12vh + 18px)',
      left: '50%',
      transform: 'translateX(-50%)',
      color: styles.messageColor,
      fontSize: styles.fontSize,
      fontWeight: '600',
      textAlign: 'center',
      zIndex: 3,
      textShadow: '0 2px 8px rgba(0, 0, 0, 0.6)',
      margin: 0,
      padding: '8px 20px',
      maxWidth: '90vw',
      backgroundColor: styles.messageBackgroundColor,
      borderRadius: '8px',
      backdropFilter: 'blur(4px)',
    },
    subtitleText: {
      position: 'fixed',
      bottom: 'calc(8vh + 18px)',
      left: '50%',
      transform: 'translateX(-50%)',
      color: styles.messageColor,
      fontSize: `calc(${styles.fontSize} * 0.8)`,
      fontWeight: '500',
      textAlign: 'center',
      zIndex: 3,
      textShadow: '0 2px 8px rgba(0, 0, 0, 0.6)',
      margin: 0,
      padding: '6px 16px',
      maxWidth: '85vw',
      backgroundColor: styles.messageBackgroundColor,
      borderRadius: '6px',
      backdropFilter: 'blur(4px)',
      opacity: 0.9,
    },
    passOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      animation: 'fadeIn 0.3s ease-in-out',
    },
    errorColor: styles.errorColor,
    successColor: styles.successColor,
  };
};

export default {
  mapBackendConfig,
  getStylesFromConfig,
  DEFAULT_CONFIG
};
